/**
 * EditableTranslation — affiche `_fr` ou `_en` (toggle externe via prop `lang`)
 * avec un crayon discret cote EN qui ouvre une `<Modal>` "Override traduction".
 *
 * Usage typique sur `WorkDetailPage` onglet Aperçu :
 *   <EditableTranslation
 *     lang={lang}
 *     valueFr={work.title_fr}
 *     valueEn={work.title_en}
 *     overridden={work.title_en_overridden}
 *     fieldLabel="Titre"
 *     onSubmit={(en) => updateWork.mutate({ title_en: en })}
 *     as="h1"
 *   />
 *
 * Comportement :
 *  - `lang="fr"` -> rend `valueFr` brut, sans crayon (la verite est en FR).
 *  - `lang="en"` -> rend `valueEn` (ou un placeholder "Traduction en cours…")
 *    avec un crayon cote droit qui ouvre la modal d'override. Si
 *    `overridden=true`, on affiche `<LangBadge>` "EN · figé".
 *
 * Le composant n'orchestre pas la mutation — il appelle `onSubmit(newEn)` et
 * laisse l'appelant gerer la query invalidation (cf TanStack Query).
 *
 * Choix de design : pas de toggle FR/EN dans ce composant. Le toggle vit
 * une seule fois sur `WorkDetailPage` (en haut du contenu textuel) et le
 * `lang` est passe par prop a tous les `<EditableTranslation>` de la page.
 * Eviter les toggles independants par champ — Stéphanie veut comparer la
 * page entiere FR vs EN, pas champ par champ.
 */

import { useState, type ElementType, type ReactNode } from 'react'
import { Pencil } from 'lucide-react'

import Button from './Button'
import LangBadge from './LangBadge'
import Modal from './Modal'
import Field from './Field'
import Textarea from './Textarea'
import Input from './Input'

import './EditableTranslation.css'

interface EditableTranslationProps {
  lang: 'fr' | 'en'
  valueFr: string | null | undefined
  valueEn: string | null | undefined
  /** True si la traduction EN a ete editee manuellement (badge "figé"). */
  overridden?: boolean
  /** Label du champ pour la modal d'override (ex: "Titre", "Description"). */
  fieldLabel: string
  onSubmit: (newEn: string) => void | Promise<void>
  /** Element rendu (h1/h2/p/...). Default: 'p'. */
  as?: ElementType
  /** Format de la modal — `single` = Input ; `multi` = Textarea. */
  format?: 'single' | 'multi'
  /** Texte affiche quand valueEn est vide en mode lang="en". */
  pendingPlaceholder?: ReactNode
  /** Classe additionnelle sur l'element de rendu. */
  className?: string
  /** Si lang="fr", masque entierement le crayon (pas de bouton edit FR). */
  hideEditOnFr?: boolean
}

export default function EditableTranslation({
  lang,
  valueFr,
  valueEn,
  overridden = false,
  fieldLabel,
  onSubmit,
  as: Tag = 'p',
  format = 'single',
  pendingPlaceholder = 'Traduction en cours…',
  className,
  hideEditOnFr = false,
}: EditableTranslationProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const display = lang === 'fr' ? valueFr : valueEn
  const isEmptyEn = lang === 'en' && (!valueEn || valueEn.trim() === '')

  // Initialiser draft au moment de l'ouverture (cohérent avec la regle
  // React 19 `set-state-in-effect` : pas de setState() dans un effect).
  const handleOpen = () => {
    setDraft(valueEn ?? '')
    setIsOpen(true)
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await onSubmit(draft)
      setIsOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  const showEditButton = lang === 'en' || !hideEditOnFr

  const containerClasses = ['editable-translation', className].filter(Boolean).join(' ')

  return (
    <div className={containerClasses}>
      <Tag className="editable-translation-value">
        {isEmptyEn ? <span className="ui-text-muted">{pendingPlaceholder}</span> : (display ?? '')}
      </Tag>

      <div className="editable-translation-meta">
        {lang === 'en' && overridden && (
          <span className="editable-translation-overridden-flag">
            <LangBadge lang="en" /> figé
          </span>
        )}
        {lang === 'en' && showEditButton && (
          <Button
            variant="quiet"
            size="sm"
            onClick={handleOpen}
            aria-label={`Surcharger la traduction anglaise du champ ${fieldLabel}`}
            title="Surcharger la traduction"
          >
            <Pencil size={14} aria-hidden="true" />
          </Button>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={`Surcharger la traduction · ${fieldLabel}`}
        kicker="Override EN"
        size="form"
      >
        <Modal.Body>
          <p className="editable-translation-hint">
            Modifier ici fige la traduction anglaise. Les futures re-extractions Vision ne
            l'écraseront plus pour ce champ.
          </p>
          <Field label={`Version FR (référence)`} hideLabel>
            {(controlProps) =>
              format === 'multi' ? (
                <Textarea {...controlProps} value={valueFr ?? ''} rows={3} readOnly />
              ) : (
                <Input {...controlProps} value={valueFr ?? ''} readOnly />
              )
            }
          </Field>
          <Field label={`Traduction EN`}>
            {(controlProps) =>
              format === 'multi' ? (
                <Textarea
                  {...controlProps}
                  value={draft}
                  rows={5}
                  onChange={(e) => setDraft(e.target.value)}
                />
              ) : (
                <Input {...controlProps} value={draft} onChange={(e) => setDraft(e.target.value)} />
              )
            }
          </Field>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={submitting}
            disabled={draft.trim() === ''}
          >
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
